import type { PatientModel } from '@ctw/shared/api/fhir/models/patient';
import type { LegacyRowActionsConfigProp } from '@ctw/shared/components/tables/legacy-table';
import {
  type FetchableResource,
  type FetchableResourceIdentifier,
  fetchResourceByTypeAndIds,
  useFqsHotstorageRequestOptions,
} from '@ctw/shared/context/_internal/resource/resource-fetch';
import { resourceModalActionForModel } from '@ctw/shared/context/_internal/resource/resource-modal';
import { type CTWRequestContext, type CTWState, useCTW } from '@ctw/shared/context/ctw-context';
import {
  type ModalLayout,
  type OpenModalAction,
  type UseModalOptions,
  useModal,
  useModalState,
} from '@ctw/shared/context/modal-context';
import {
  usePatientContextIfAvailable,
  withPatientContext,
} from '@ctw/shared/context/patient-provider';
import {
  type TelemetryContext,
  useTelemetry,
} from '@ctw/shared/context/telemetry/telemetry-boundary';
import type { OverlayLoadTrigger } from '@ctw/shared/context/use-overlay-load-metric';
import { useZuiContext } from '@ctw/shared/context/zui-provider';
import {
  type FetchableModelType,
  type FetchableResourceType,
  isFetchableResourceType,
  isValidResourceId,
  type ResourceTypeModelMap,
} from '@ctw/shared/utils/fhir/resource-mappings';
import { pathWithParameters } from '@ctw/shared/utils/url';
import type { QueryClient } from '@tanstack/query-core';
import { useQueryClient } from '@tanstack/react-query';
import type { GraphQLClient } from 'graphql-request';
import { isEmpty } from 'lodash-es';
import {
  createContext,
  Fragment,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigationType } from 'react-router';
import type { SetOptional } from 'type-fest';

export interface ResourceModalState<
  TResourceType extends FetchableResourceType = FetchableResourceType,
  TModel extends FetchableModelType<TResourceType> = FetchableModelType<TResourceType>,
> {
  isModalOpen: boolean;
  openResourceModal: (options: UseResourceModalOpenModalArgs<TResourceType>) => void;
  reloadResource: (
    loadTrigger?: OverlayLoadTrigger,
  ) => Promise<FetchableModelType<TResourceType> | null>;
  urlDeepLinkResource: FetchableResource | null;
  fetchedDeepLinkResource: TModel | null;
  setUrlDeepLinkResource: (specifier: FetchableResourceIdentifier) => void;
  setModal: (Modal: ReturnType<typeof useModal>) => void;
}

const ResourceModalContext = createContext<ResourceModalState | null>(null);

export const useResourceModalIfAvailable = () => useContext(ResourceModalContext);

const defaultResourceModalLayout: ModalLayout = {
  mode: 'full',
  overflowBehavior: 'scroll',
  fullbleed: true,
  showCloseButton: false,
} as const;

/**
 * - `off`: provider neither reads deep-link URL params on mount nor writes
 *   them when a modal opens. The modal opens locally without touching URL.
 * - `write-only`: provider writes URL params on explicit opens (so the URL
 *   stays shareable) but does NOT auto-open from URL on mount. Use for
 *   nested providers when an outer provider already handles URL-mount
 *   auto-open — prevents duplicate opens.
 * - `read-and-write` (default): full deep linking. Read URL on mount and
 *   auto-open the matching modal; write URL on explicit opens.
 */
export type DeepLinkBehavior = 'off' | 'write-only' | 'read-and-write';

interface ResourceModalProviderProps extends PropsWithChildren {
  deepLinkBehavior?: DeepLinkBehavior;
  defaultLayout?: ModalLayout;
}

export const ResourceModalProvider = <
  TResourceType extends FetchableResourceType = FetchableResourceType,
  TModel extends FetchableModelType<TResourceType> = FetchableModelType<TResourceType>,
>({
  deepLinkBehavior = 'read-and-write',
  defaultLayout = defaultResourceModalLayout,
  children,
}: ResourceModalProviderProps) => {
  const providerTelemetry = useTelemetry();
  const { navigate } = useZuiContext();
  const routerLocation = useLocation();
  const locationRef = useRef(routerLocation);
  locationRef.current = routerLocation;
  const { requestContext, graphqlClient, ctwFetch } = useCTW();
  const queryClient = useQueryClient();
  const fqsHotstorageRequestOptions = useFqsHotstorageRequestOptions();
  const liveModalState = useModalState();
  const [urlDeepLinkResource, internalSetUrlDeepLinkResource] = useState<FetchableResource | null>(
    null,
  );
  // Must be a ref, not state: `closeModal` below reads it at call time and must always see the
  // boundary from the most recent open, not a snapshot captured before any modal ever opened.
  const modalRef = useRef<ReturnType<typeof useModal> | null>(null);
  const liveModalStateRef = useRef(liveModalState);
  liveModalStateRef.current = liveModalState;
  // A `ModalProvider` above is always current; without one, the modal a consumer registered is.
  const getModalToClose = useCallback(() => {
    const liveModalStateNow = liveModalStateRef.current;
    if (liveModalStateNow.isProvided) {
      return {
        isModalOpen: liveModalStateNow.isModalOpen,
        boundaryName: liveModalStateNow.boundary.name,
        closeModal: liveModalStateNow.closeModal,
      };
    }

    const registeredModal = modalRef.current;
    return registeredModal
      ? {
          isModalOpen: registeredModal.isModalOpen,
          boundaryName: registeredModal.modalBoundary.name,
          closeModal: registeredModal.closeModal,
        }
      : null;
  }, []);
  const hasRegisteredResourceModalCloseListenerRef = useRef(false);
  const patientContext = usePatientContextIfAvailable();
  const [fetchedDeepLinkResource, setFetchedDeepLinkResource] = useState<TModel | null>(null);
  const deepLinkBehaviorRef = useRef(deepLinkBehavior);
  deepLinkBehaviorRef.current = deepLinkBehavior;
  const isFirstUrlSyncRef = useRef(true);
  const navigationType = useNavigationType();
  const urlDeepLinkResourceRef = useRef(urlDeepLinkResource);
  urlDeepLinkResourceRef.current = urlDeepLinkResource;
  const canFetchDeepLinkedResource = useMemo(
    () => isEmpty(fetchedDeepLinkResource) && !isEmpty(urlDeepLinkResource) && patientContext,
    [patientContext, fetchedDeepLinkResource, urlDeepLinkResource],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: Only once to set default on first render
  useEffect(() => {
    modalRef.current?.setLayout(defaultLayout);
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Don't re-run when `navigate` changes
  const removeUrlDeepLinkResource = useCallback(() => {
    const urlSearchParams = new URLSearchParams(locationRef.current.search);
    const urlResourceType = urlSearchParams.get(
      'modalResourceType',
    ) as FetchableResourceType | null;
    const urlResourceId = urlSearchParams.get('modalResourceId');

    if (deepLinkBehaviorRef.current !== 'off' && (urlResourceType || urlResourceId)) {
      navigate(
        pathWithParameters(`${locationRef.current.pathname}${locationRef.current.search}`, {
          modalResourceType: null,
          modalResourceId: null,
        }),
        { replace: true },
      );
    }
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: reloadResource and setUrlDeepLinkResource intentionally omitted to keep openResourceModal stable across renders
  const openResourceModal = useCallback(
    ({
      model,
      patient: explicitPatient,
      resourceId: explicitResourceId,
      resourceType: explicitResourceType,
      ...openModalArgs
    }: UseResourceModalOpenModalArgs<TResourceType>) => {
      const resourceId = (model ? model.id : explicitResourceId) ?? '';
      const resourceType = (model ? model.resourceType : explicitResourceType) ?? '';
      const patient = explicitPatient ?? patientContext?.patient;
      // Start the modal load time clock here rather than at the open itself, so that the model
      // fetch below — which the user is already waiting on — counts toward it.
      const loadStartedAt = openModalArgs.loadStartedAt ?? Date.now();
      const loadTrigger = openModalArgs.loadTrigger ?? 'user';

      const urlSearchParams = new URLSearchParams(locationRef.current.search);
      const urlResourceId = urlSearchParams.get('modalResourceId');

      if (deepLinkBehavior !== 'off' && urlResourceId !== resourceId) {
        setUrlDeepLinkResource({ resourceType, resourceId });
      }

      const effectiveTelemetry = openModalArgs.telemetry ?? providerTelemetry;
      effectiveTelemetry.setScopedContextProperties({
        ...openModalArgs.telemetryContext,
        resourceId,
        resourceType,
      });

      const performCanonicalAction = (actionModel: TModel, actionPatient: PatientModel) => {
        const canonicalAction =
          openModalArgs.action?.({
            patient: actionPatient,
            ctwFetch,
            graphqlClient,
            requestContext,
            telemetry: effectiveTelemetry,
            queryClient,
            closeModal: () => modalRef.current?.closeModal(),
            model: actionModel as never,
            reloadResource: reloadResource as never,
          }) ??
          resourceModalActionForModel(actionModel)?.({
            patient: actionPatient,
            ctwFetch,
            graphqlClient,
            requestContext,
            telemetry: effectiveTelemetry,
            queryClient,
            closeModal: () => modalRef.current?.closeModal(),
            model: actionModel as never,
            reloadResource: reloadResource as never,
          });

        if (modalRef.current && canonicalAction) {
          // Mark the resource as already-fetched so the deep-link
          // auto-open useEffect doesn't race this explicit open and
          // re-fire (which would open the modal a second time).
          setFetchedDeepLinkResource(actionModel);
          // Modals are portaled outside the opener's DOM tree, so a modal
          // opened from inside a drawer would otherwise lose the drawer's
          // patient context. Wrap the body so any descendant can
          // `usePatientContext` against the resource's patient.
          // The body is wrapped in a Fragment keyed by the resource so the
          // body is always remounted when the resource changes
          modalRef.current.openModal({
            ...openModalArgs,
            /* Naming the resource on the modal's telemetry boundary is what makes every query,
             * interaction, error, and log emitted by the modal's content attributable to the
             * modal it came from. The model fetch above runs before that boundary exists and so
             * carries no such annotation; modal load time is what covers that window.
             */
            telemetryContext: {
              ...openModalArgs.telemetryContext,
              modalResourceType: resourceType,
              modalResourceId: resourceId,
            },
            action: {
              ...canonicalAction,
              loadStartedAt,
              loadTrigger,
              content: {
                body: withPatientContext(
                  <Fragment key={`${actionModel.resourceType}:${actionModel.id}`}>
                    {canonicalAction.content.body}
                  </Fragment>,
                  actionPatient.UPID,
                ),
              },
            },
          });
        }
      };

      if (model && patient) {
        performCanonicalAction(model as never, patient);
      } else if (patient) {
        void fetchResourceByTypeAndIds({
          resourceType: resourceType as never,
          resourceIds: [resourceId],
          ctwFetch,
          graphqlClient,
          requestContext,
          queryClient,
          patient,
          telemetry: effectiveTelemetry,
          fqsHotstorageRequestOptions,
        }).then((fetchedResources) => {
          performCanonicalAction(fetchedResources[0], patient);
        });
      } else {
        effectiveTelemetry.trackError({
          error: new Error('Cannot open resource modal: patient context required'),
          context: { resourceId, resourceType },
        });
        throw new Error(
          `Resource modal requires patient context. ResourceType: ${resourceType}, ResourceId: ${resourceId}`,
        );
      }
    },
    [
      ctwFetch,
      graphqlClient,
      requestContext,
      queryClient,
      providerTelemetry,
      patientContext,
      deepLinkBehavior,
      fqsHotstorageRequestOptions,
    ],
  );

  const fetchResource = useCallback(async (): Promise<TModel | null> => {
    if (!(canFetchDeepLinkedResource && urlDeepLinkResource?.resourceId)) {
      return null;
    }

    const reloadedResource = patientContext?.patient
      ? (
          await fetchResourceByTypeAndIds({
            telemetry: providerTelemetry,
            ctwFetch,
            graphqlClient,
            requestContext,
            queryClient,
            patient: patientContext?.patient,
            resourceType: urlDeepLinkResource.resourceType,
            resourceIds: [urlDeepLinkResource.resourceId],
            fqsHotstorageRequestOptions,
          })
        )[0]
      : null;

    return reloadedResource as never;
  }, [
    canFetchDeepLinkedResource,
    providerTelemetry,
    patientContext,
    ctwFetch,
    graphqlClient,
    requestContext,
    queryClient,
    urlDeepLinkResource?.resourceId,
    urlDeepLinkResource?.resourceType,
    fqsHotstorageRequestOptions,
  ]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: reloadResource self-reference intentionally omitted to keep reloadResource stable across renders
  const reloadResource = useCallback(
    async (loadTrigger: OverlayLoadTrigger = 'reload') => {
      const loadStartedAt = Date.now();
      const fetchedResource = await fetchResource();

      setFetchedDeepLinkResource(fetchedResource);

      if (fetchedResource) {
        openResourceModal({
          telemetry: providerTelemetry,
          model: fetchedResource as never,
          reloadResource,
          loadStartedAt,
          loadTrigger,
        });
      }

      return fetchedResource;
    },
    [fetchResource, providerTelemetry, openResourceModal],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: Don't re-run when `navigate` changes
  const setUrlDeepLinkResource = useCallback(
    ({ resourceType, resourceId }: FetchableResourceIdentifier) => {
      if (isFetchableResourceType(resourceType) && isValidResourceId(resourceId)) {
        const currentSearchParams = new URLSearchParams(locationRef.current.search);
        if (
          currentSearchParams.get('modalResourceType') !== resourceType ||
          currentSearchParams.get('modalResourceId') !== resourceId
        ) {
          navigate(
            pathWithParameters(`${locationRef.current.pathname}${locationRef.current.search}`, {
              modalResourceType: resourceType,
              modalResourceId: resourceId,
            }),
          );
        }
        if (resourceId !== urlDeepLinkResource?.resourceId) {
          internalSetUrlDeepLinkResource({ resourceType, resourceId });
        }
      }
    },
    [reloadResource, urlDeepLinkResource],
  );

  const urlResourceType = new URLSearchParams(routerLocation.search).get('modalResourceType');
  const urlResourceId = new URLSearchParams(routerLocation.search).get('modalResourceId');
  useEffect(() => {
    const isMount = isFirstUrlSyncRef.current;
    isFirstUrlSyncRef.current = false;
    // Mount and history traversal sync the URL; this provider's own writers update state directly.
    if (!(isMount || navigationType === 'POP')) {
      return;
    }

    if (!urlResourceId) {
      internalSetUrlDeepLinkResource(null);
      setFetchedDeepLinkResource(null);
      const modalToClose = getModalToClose();
      if (
        deepLinkBehavior !== 'off' &&
        modalToClose?.isModalOpen &&
        modalToClose.boundaryName === 'ResourceModal'
      ) {
        modalToClose.closeModal();
      }
      return;
    }

    if (
      deepLinkBehavior === 'read-and-write' &&
      urlResourceId !== urlDeepLinkResourceRef.current?.resourceId
    ) {
      setFetchedDeepLinkResource(null);
      if (urlResourceType) {
        internalSetUrlDeepLinkResource({
          resourceType: urlResourceType as FetchableResourceType,
          resourceId: urlResourceId,
        });
      }
    }
  }, [urlResourceId, urlResourceType, deepLinkBehavior, navigationType, getModalToClose]);

  useEffect(() => {
    if (
      urlDeepLinkResource?.resourceId &&
      (!fetchedDeepLinkResource?.id ||
        fetchedDeepLinkResource.id !== urlDeepLinkResource.resourceId)
    ) {
      const loadStartedAt = Date.now();

      void fetchResource().then((fetchedResource) => {
        if (fetchedResource) {
          setFetchedDeepLinkResource(fetchedResource);
          openResourceModal({
            telemetry: providerTelemetry,
            model: fetchedResource,
            reloadResource,
            loadStartedAt,
            loadTrigger: 'deep-link',
          });
        }
      });
    }
  }, [
    urlDeepLinkResource,
    fetchResource,
    fetchedDeepLinkResource,
    openResourceModal,
    reloadResource,
    providerTelemetry,
  ]);

  return (
    <ResourceModalContext.Provider
      value={{
        isModalOpen: liveModalState.isModalOpen,
        openResourceModal: openResourceModal as never,
        reloadResource,
        urlDeepLinkResource,
        setUrlDeepLinkResource,
        fetchedDeepLinkResource,
        setModal: (newModal) => {
          modalRef.current = newModal;
          if (!hasRegisteredResourceModalCloseListenerRef.current) {
            hasRegisteredResourceModalCloseListenerRef.current = true;
            newModal.onModalEvent('ResourceModal', (eventType) => {
              if (eventType === 'close') {
                internalSetUrlDeepLinkResource(null);
                setFetchedDeepLinkResource(null);
                requestAnimationFrame(() => removeUrlDeepLinkResource());
              }
            });
            // The only thing this initial reload can open is a resource named in the URL.
            void reloadResource('deep-link');
          }
        },
      }}
    >
      {children}
    </ResourceModalContext.Provider>
  );
};

export interface OpenResourceModalAction extends OpenModalAction {}

interface UseResourceModalCommonOpenModalArgs<
  TResourceType extends FetchableResourceType,
  TModel extends FetchableModelType<TResourceType> = FetchableModelType<TResourceType>,
> extends SetOptional<OpenModalAction, 'content'> {
  telemetry: ReturnType<typeof useTelemetry>;
  telemetryContext?: TelemetryContext;
  reloadResource?: () => Promise<TModel | null>;
  patient?: PatientModel;
  action?: (args: UseResourceModalActionArgs<TResourceType, TModel>) => OpenResourceModalAction;
}

interface UseResourceModalOpenWithModelModalArgs<
  TResourceType extends FetchableResourceType,
  TModel extends FetchableModelType<TResourceType> = FetchableModelType<TResourceType>,
> extends UseResourceModalCommonOpenModalArgs<TResourceType, TModel> {
  model: TModel;
  resourceId?: never;
  resourceType?: never;
}

interface UseResourceModalOpenWithResourceTypeAndIdModalArgs<
  TResourceType extends FetchableResourceType,
  TModel extends FetchableModelType<TResourceType> = FetchableModelType<TResourceType>,
> extends UseResourceModalCommonOpenModalArgs<TResourceType, TModel> {
  resourceId: string;
  resourceType: string;
  model?: never;
}

export type UseResourceModalOpenModalArgs<
  TResourceType extends FetchableResourceType,
  TModel extends FetchableModelType<TResourceType> = FetchableModelType<TResourceType>,
> =
  | UseResourceModalOpenWithModelModalArgs<TResourceType, TModel>
  | UseResourceModalOpenWithResourceTypeAndIdModalArgs<TResourceType, TModel>;

export interface UseResourceModalActionArgs<
  TResourceType extends FetchableResourceType,
  TModel extends FetchableModelType<TResourceType>,
> {
  ctwFetch: CTWState['ctwFetch'];
  graphqlClient: GraphQLClient;
  requestContext: CTWRequestContext;
  telemetry: ReturnType<typeof useTelemetry>;
  queryClient: QueryClient;
  model: TModel;
  patient: PatientModel;
  closeModal: () => void;
  reloadResource: () => Promise<TModel | null>;
}

export interface UseResourceModalOptions<
  TResourceType extends FetchableResourceType,
  TModel extends FetchableModelType<TResourceType> = FetchableModelType<TResourceType>,
> extends Omit<UseModalOptions, 'boundaryName'> {
  resourceActions?: TResourceType extends keyof ResourceTypeModelMap
    ? (model: TModel) => LegacyRowActionsConfigProp<TModel>
    : never;
  enableViewFhirButton?: boolean;
  enableReadActions?: boolean;
  action?: (args: UseResourceModalActionArgs<TResourceType, TModel>) => OpenResourceModalAction;
}

const defaultUseResourceModalLayout: ModalLayout = {
  mode: 'full',
  overflowBehavior: 'hidden',
  fullbleed: true,
  showCloseButton: false,
} as const;

export const useResourceModal = <TResourceType extends FetchableResourceType>({
  layout = defaultUseResourceModalLayout,
  telemetryContext: hookExplicitTelemetryContext,
  onModalEvent,
  resourceActions,
  enableViewFhirButton = true,
  enableReadActions = false,
  action,
}: UseResourceModalOptions<TResourceType> = {}) => {
  const context = useContext(ResourceModalContext);

  if (!context) {
    throw new Error('useResourceModal must be used inside a ResourceModalContext');
  }

  const hookLocalTelemetry = useTelemetry();
  // Resolve `PatientContext` at the call site so subtrees re-scoped by
  // `ScopedPatientProvider` (e.g. drawer view bodies) supply their own
  // patient to the outer `ResourceModalProvider` — whose own
  // `usePatientContextIfAvailable` lookup sits above the scoped provider
  // and would otherwise see no patient.
  const hookLocalPatientContext = usePatientContextIfAvailable();

  const modal = useModal({
    boundaryName: 'ResourceModal',
    layout,
    telemetryContext: {
      ...hookLocalTelemetry.getScopedContextProperties(),
      ...hookExplicitTelemetryContext,
      withinResourceModal: true,
    },
    onModalEvent,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: Can't change these options after mount
  useEffect(() => {
    context.setModal(modal);
  }, [modal]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Can't change these options after mount
  const openResourceModal = useCallback(
    (options: UseResourceModalOpenModalArgs<TResourceType>) => {
      const resourceId = options?.model?.id ?? options.resourceId;
      const resourceType = options?.model?.resourceType ?? options.resourceType;

      requestAnimationFrame(() => {
        context.openResourceModal({
          action,
          resourceActions,
          enableViewFhirButton,
          enableReadActions,
          patient: hookLocalPatientContext?.patient,
          ...options,
          telemetryContext: {
            ...hookLocalTelemetry.getScopedContextProperties(),
            ...hookExplicitTelemetryContext,
            ...options.telemetryContext,
            resourceId,
            resourceType,
            withinResourceModal: true,
          },
        } as never);
      });
    },
    [modal, action, context, hookLocalPatientContext?.patient],
  );

  return {
    setUrlDeepLinkResource: context.setUrlDeepLinkResource,
    openResourceModal,
  };
};
